import { useMemo } from "react";

import { pluralize } from "../../../helpers/helpers";
import { Product, SubscriptionAddon } from "../types";
import { getPrice } from "../utils";

// Display order of the primary cap metrics — must match the `features`
// array below (Sites, Team Members, Keywords, Pages).
const METRIC_ORDER = ["site_max", "membership_max", "keyword_max", "page_max"];
const metricRank = (key: string | undefined) => {
  const index = METRIC_ORDER.indexOf(key ?? "");

  return index === -1 ? METRIC_ORDER.length : index;
};

export const usePricingPlans = (
  products: Product[],
  subscriptionAddons: SubscriptionAddon[],
) => {
  return useMemo(() => {
    const plans = products?.filter((p) => p.kind === "plan");

    return plans?.map((plan) => {
      const price = plan.prices;
      const optionalAddons = plan.optional_addons.map((addon) => {
        const product = products?.find((el) => el.id === addon?.product_id);
        const productPrice = product?.prices.find(
          (price) => price.tier === plan.tier,
        );
        const price = productPrice?.amount
          ? getPrice(productPrice?.amount, productPrice?.currency)
          : productPrice?.tiers?.map((tier) => tier?.unit_amount_decimal);
        const productKey = product?.key;
        const subscriptionProdunct = subscriptionAddons?.find(
          (el) => el?.price?.product?.key === productKey,
        );

        const optionalAddon = {
          id: addon.id,
          key: product?.key,
          name: product?.name,
          price,
          pureProductPrice: productPrice?.amount,
          currency: productPrice?.currency,
          interval: productPrice?.recurring_interval,
          max: productPrice?.divide_by,
          quantity: subscriptionProdunct?.quantity || 0,
          sliderRangeTitle: `${product?.name} - ${price} per ${productPrice?.divide_by} ${pluralize(product?.label || "", productPrice?.divide_by || 1)}`,
          priceId: productPrice?.id,
          divide_by: productPrice?.divide_by,
          subscriptionProdunctId: subscriptionProdunct?.id,
          singleton: !!product?.singleton,
        };

        return optionalAddon;
      });
      // Cap-extending add-ons follow the feature-list order; others keep
      // their API order after them (sort is stable). The entries are
      // prices, which carry no key — resolve it through the product.
      const productKeyFor = (addon: { product_id: number }) =>
        products.find((el) => el.id === addon.product_id)?.key;
      const defaultAddons = [...plan.default_addons]
        .sort(
          (a, b) => metricRank(productKeyFor(a)) - metricRank(productKeyFor(b)),
        )
        .map((addon) => {
          const product = products.find((el) => el.id === addon.product_id);
          const productPrice = product?.prices.find(
            (price) => price.tier === plan.tier,
          );
          const productKey = product?.key;
          const subscriptionProdunct = subscriptionAddons?.find(
            (el) => el.price.product.key === productKey,
          );

          const defaultAddon = {
            id: addon.id,
            // Prices carry no key of their own — use the product's
            key: productKey,
            label: product?.label,
            name: product?.name,
            price: productPrice,
            currency: productPrice?.currency,
            interval: productPrice?.recurring_interval,
            max: productPrice?.divide_by,
            quantity: subscriptionProdunct?.quantity || 0,
            divide_by: productPrice?.divide_by,
            subscriptionProdunctId: subscriptionProdunct?.id,
          };

          return defaultAddon;
        });

      const getTooltipContent = (key: string) => {
        const addonFeature = products.find((addon) => addon?.key === key);
        const addonFeaturePrice = addonFeature?.prices.find(
          (price) => price.tier === plan.tier,
        );
        const price = getPrice(
          addonFeaturePrice?.amount || 0,
          addonFeaturePrice?.currency || "usd",
        );

        return `${price} per ${addonFeaturePrice?.divide_by} ${addonFeature?.name}`;
      };
      const featuresPlus = [
        {
          key: "api_access",
          value: plan.api_access,
        },
        {
          key: "crawl_interval",
          value: plan.crawl_interval,
        },
        {
          key: "refresh_interval",
          value: plan.refresh_interval,
        },
      ];
      const features = [
        {
          key: "site_max",
          value: plan.site_max,
          infoIconText: getTooltipContent("site_max"),
        },
        {
          key: "membership_max",
          value: plan.membership_max,
        },
        {
          key: "keyword_max",
          value: plan.keyword_max,
          infoIconText: getTooltipContent("keyword_max"),
        },
        {
          key: "page_max",
          value: plan.page_max,
          infoIconText: getTooltipContent("page_max"),
        },
      ];

      const planInfo = {
        id: plan.id,
        name: plan.name,
        description: plan.description,
        highlight: plan.highlight,
        price: plan.prices[0].amount || 0,
        interval: plan.prices[0].recurring_interval || "",
        currency: plan.prices[0]?.currency || "usd",
        priceId: plan.prices[0].id,
        demo: plan?.demo,
      };
      if (!price) {
        return null;
      }

      return {
        ...planInfo,
        features: features,
        featuresPlus: featuresPlus,
        addons: {
          optionalAddons,
          defaultAddons,
        },
      };
    });
  }, [products, subscriptionAddons]);
};
